import apiClient from './api';

export interface BlogArticle {
  id: number;
  author_id: number;
  author_name: string | null;
  title: string;
  excerpt: string | null;
  content: string;
  cover_color: string;
  cover_image: string | null;
  event_type_id: number | null;
  event_type_name: string | null;
  event_type_emoji: string | null;
  city_id: number | null;
  city_name: string | null;
  status: 'draft' | 'published';
  views_count: number;
  created_at: string | null;
  published_at: string | null;
  reactions: Record<string, number>;
  comments_count: number;
  comments?: BlogComment[];
  can_edit?: boolean;
  can_delete?: boolean;
}

export interface BlogComment {
  id: number;
  user_id: number;
  user_name: string | null;
  content: string;
  parent_comment_id: number | null;
  reactions: Record<string, number>;
  created_at: string | null;
}

export interface BlogAccess {
  allowed: boolean;
  reason: string | null;
}

export const blogService = {
  async listArticles(params: {
    author_id?: number; event_type_id?: number; city_id?: number;
    date_from?: string; date_to?: string; limit?: number; offset?: number;
  } = {}): Promise<{ articles: BlogArticle[]; total: number }> {
    const res = await apiClient.get('/api/blog/articles', { params });
    return res.data;
  },

  async getArticle(id: number): Promise<BlogArticle> {
    const res = await apiClient.get(`/api/blog/articles/${id}`);
    return res.data.article;
  },

  async getMyArticle(id: number): Promise<BlogArticle> {
    const res = await apiClient.get(`/api/blog/my/articles/${id}`);
    return res.data.article;
  },

  async createArticle(data: {
    title: string; excerpt?: string; content: string; cover_color?: string;
    cover_image?: string | null; event_type_id?: number | null; city_id?: number | null; status?: string;
  }): Promise<BlogArticle> {
    const res = await apiClient.post('/api/blog/articles', data);
    return res.data.article;
  },

  async updateArticle(id: number, data: Partial<{
    title: string; excerpt: string; content: string; cover_color: string;
    cover_image: string | null; event_type_id: number | null; city_id: number | null; status: string;
  }>): Promise<BlogArticle> {
    const res = await apiClient.put(`/api/blog/articles/${id}`, data);
    return res.data.article;
  },

  async deleteArticle(id: number): Promise<void> {
    await apiClient.delete(`/api/blog/articles/${id}`);
  },

  async listMyArticles(): Promise<BlogArticle[]> {
    const res = await apiClient.get('/api/blog/my/articles');
    return res.data.articles;
  },

  async hasMyArticles(): Promise<boolean> {
    const res = await apiClient.get('/api/blog/my/has-articles');
    return res.data.has_articles;
  },

  async setReaction(articleId: number, reaction: string): Promise<{ reactions: Record<string, number>; user_reaction: string | null }> {
    const res = await apiClient.post(`/api/blog/articles/${articleId}/reactions`, { reaction });
    return res.data;
  },

  async setCommentReaction(commentId: number, reaction: string): Promise<{ reactions: Record<string, number>; user_reaction: string | null }> {
    const res = await apiClient.post(`/api/blog/comments/${commentId}/reactions`, { reaction });
    return res.data;
  },

  async addComment(articleId: number, content: string, parentCommentId?: number): Promise<BlogComment> {
    const res = await apiClient.post(`/api/blog/articles/${articleId}/comments`, { content, parent_comment_id: parentCommentId });
    return res.data.comment;
  },

  async deleteComment(commentId: number): Promise<void> {
    await apiClient.delete(`/api/blog/comments/${commentId}`);
  },

  async checkAccess(): Promise<BlogAccess> {
    const res = await apiClient.get('/api/blog/access');
    return res.data;
  },

  // Admin
  async listBans(): Promise<any[]> {
    const res = await apiClient.get('/api/blog/admin/bans');
    return res.data.bans;
  },

  async createBan(data: { user_id: number; reason: string; reason_visible?: boolean; ban_type: string; duration_hours?: number; ban_scope?: string }): Promise<void> {
    await apiClient.post('/api/blog/admin/bans', data);
  },

  async deleteBan(banId: number): Promise<void> {
    await apiClient.delete(`/api/blog/admin/bans/${banId}`);
  },

  async deleteBanByUserScope(userId: number, scope: string): Promise<void> {
    await apiClient.delete(`/api/blog/admin/bans/user/${userId}/scope/${scope}`);
  },

  async listSettings(): Promise<any[]> {
    const res = await apiClient.get('/api/blog/admin/settings');
    return res.data.settings;
  },

  async updateSetting(key: string, value: string): Promise<void> {
    await apiClient.put(`/api/blog/admin/settings/${key}`, { setting_value: value });
  },

  // Subscriptions
  async listSubscriptions(): Promise<any[]> {
    const res = await apiClient.get('/api/blog/subscriptions');
    return res.data.subscriptions;
  },

  async createSubscription(data: { event_type_id?: number; author_id?: number; city_id?: number; delivery_method?: string }): Promise<void> {
    await apiClient.post('/api/blog/subscriptions', data);
  },

  async deleteSubscription(id: number): Promise<void> {
    await apiClient.delete(`/api/blog/subscriptions/${id}`);
  },

  async updateSubscription(id: number, data: { event_type_id?: number; author_id?: number; city_id?: number; delivery_method?: string }): Promise<void> {
    await apiClient.put(`/api/blog/subscriptions/${id}`, data);
  },

  // Image upload
  async uploadImage(file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post('/api/blog/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  // Polls
  async createPoll(articleId: number, question: string, options: string[]): Promise<{ poll_id: number }> {
    const res = await apiClient.post(`/api/blog/articles/${articleId}/polls`, { question, options });
    return res.data;
  },

  async getPolls(articleId: number): Promise<any[]> {
    const res = await apiClient.get(`/api/blog/articles/${articleId}/polls`);
    return res.data.polls;
  },

  async votePoll(pollId: number, optionId: number): Promise<void> {
    await apiClient.post(`/api/blog/polls/${pollId}/vote`, { option_id: optionId });
  },

  async deletePoll(pollId: number): Promise<void> {
    await apiClient.delete(`/api/blog/polls/${pollId}`);
  },

  async getPollVotes(pollId: number): Promise<any> {
    const res = await apiClient.get(`/api/blog/polls/${pollId}/votes`);
    return res.data;
  },
};